import { get, post, put, del, upload } from '@api/request';

export const fetchRollCorrections = async (
  { appraisalService },
  authenticationToken,
  { year },
) => {
  const url = `${appraisalService}/appraisal/year/${year}/rollcorrections`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export async function getRequest(
  { appraisalService },
  authenticationToken,
  { runID },
) {
  const url = `${appraisalService}/export/openrecordsrun/${runID}`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function createRequest(
  { appraisalService },
  authenticationToken,
  { payload },
) {
  const url = `${appraisalService}/export/openrecordsruns`;

  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function getOpenRecords(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/export/openrecordsruns`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export const downloadExportFile = async (
  { appraisalService },
  authenticationToken,
  { s3ID },
) => {
  const url = `${appraisalService}/export/file/${s3ID}`;
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export async function searchMarketArea(
  { appraisalService },
  authenticationToken,
  { pageSize, page, payload },
) {
  const url = `${appraisalService}/appraisal/marketarea/search?page=${page}&pageSize=${pageSize}`;

  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchMarketAreaGeneralInfo(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/generalinfo`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateMarketAreaGeneralInfo(
  { appraisalService },
  authenticationToken,
  { marketArea, year, payload },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/generalinfo`;

  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchMarketAreaStatistics(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/statistics`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function processMarketAreaStatistics(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/statistics`;

  const { body } = await post(authenticationToken, url);
  return body;
}

export async function fetchMarketAreaSubdivisions(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/abstractsubdivisions`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSalesTypes(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/codes?type=Sales&name=Sales Type`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSalesSource(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/codes?type=Sales&name=Source`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleQualifiers(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/codes?type=Sales&name=Sales%20Qualify`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchRatioTypes(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/codes?type=Sales&name=Ratio%20Type`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleSearchParameters(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/salesparameters`;

  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateSaleSearchParameters(
  { appraisalService },
  authenticationToken,
  { marketArea, year, payload },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/salesparameters`;

  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchSales(
  { appraisalService },
  authenticationToken,
  { marketArea, year, payload },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/sales`;

  let { body } = await post(authenticationToken, url, payload);
  if (
    body &&
    body.results &&
    body.results.saleRatioStats &&
    body.results.sales &&
    body.results.sales.length > 0
  ) {
    let prd = 0;
    let rowData = body.results.sales;
    if (rowData && rowData.length > 0) {
      let marketSum = 0;
      let saleSum = 0;
      let meanRatio = 0;
      let count = 0;
      for (let x = 0; x < rowData.length; x++) {
        if (!rowData[x].outlier) {
          marketSum += rowData[x].marketValue;
          saleSum += rowData[x].salePriceAdjusted;
          count++;
          meanRatio += rowData[x].salePriceAdjusted
            ? rowData[x].marketValue / rowData[x].salePriceAdjusted
            : 0;
        }
      }
      meanRatio = count !== 0 ? meanRatio / count : 0;
      const weightedMean = marketSum / saleSum;
      prd = weightedMean !== 0 ? meanRatio / weightedMean : 0;
      body.results.saleRatioStats.prd = prd.toFixed(2);
      body.results.saleRatioStats.weightedMean = weightedMean.toFixed(4);
    }
  }
  return body;
}

export async function fetchBuildingPermitSummary(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/buildingpermitsummary`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchBuilders(
  { appraisalService },
  authenticationToken,
  { marketArea },
) {
  const url = `${appraisalService}/appraisal/marketarea/${marketArea}/builders`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchRechecks(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/rechecks`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchProtestCounts(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/protestcount`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchTrendAnalysisSales(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url =
    `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}` +
    `/trendanalysissales`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchMarketAreaPopulation(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const data = { current: null, prior: null };
  const currentUrl = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/population`;
  const priorUrl = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year -
    1}/population`;

  const { body: currentBody } = await get(authenticationToken, currentUrl);
  data.current = currentBody;

  const { body: priorBody } = await get(authenticationToken, priorUrl);
  data.prior = priorBody;

  return data;
}

export async function analyzeSales(
  { appraisalService },
  authenticationToken,
  { marketArea, year, payload },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/analyze`;

  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function applyModifierAndRecalculate(
  { recalculationService },
  authenticationToken,
  { payload },
) {
  const url = `${recalculationService}/recalc/applymodifierlaunchjob`;

  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function resetModifierAndRecalculate(
  { recalculationService },
  authenticationToken,
  { payload },
) {
  const url = `${recalculationService}/recalc/resetmodifierlaunchjob`;

  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchRecalculationJobStatus(
  { recalculationService },
  authenticationToken,
  { jobID },
) {
  const url = `${recalculationService}/recalc/job/${jobID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchAppraisers({ userService }, authenticationToken) {
  const url = `${userService}/trueprodigy/user/appraisal/byattribute/Appraiser`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchCodeFile(
  { appraisalService },
  authenticationToken,
  { year, type, name },
) {
  let url = `${appraisalService}/appraisal/codes?type=${type}&name=${name}`;
  url += year ? `&year=${year}` : '';
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function insertMarketAreaBuilder(
  { appraisalService },
  authenticationToken,
  { marketArea, payload },
) {
  const url = `${appraisalService}/appraisal/marketarea/${marketArea}/builders`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function deleteMarketAreaBuilder(
  { appraisalService },
  authenticationToken,
  { marketArea, builder },
) {
  const url = `${appraisalService}/appraisal/marketareabuilder/${marketArea}/${builder}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function setSaleAsOutlier(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/setoutlier`;
  const { body } = await put(authenticationToken, url);
  return body;
}

export async function removeSaleAsOutlier(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/removeoutlier`;
  const { body } = await put(authenticationToken, url);
  return body;
}

export async function submitMarketAreaDetail(
  { appraisalService },
  authenticationToken,
  { marketArea, year, payload },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/submit`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function reopenMarketArea(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/reopen`;
  const { body } = await put(authenticationToken, url);
  return body;
}

export async function fetchSubmissionStatistics(
  { appraisalService },
  authenticationToken,
  { marketArea, year },
) {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/submitstatistics`;
  let { body } = await get(authenticationToken, url);
  if (
    body &&
    body.results &&
    body.results[0] &&
    body.results[0].submittedAnalysisStatistics &&
    body.results[0].submittedAnalysisSales &&
    body.results[0].submittedAnalysisSales.length > 0
  ) {
    let prd = 0;
    let rowData = body.results[0].submittedAnalysisSales;
    if (rowData && rowData.length > 0) {
      let marketSum = 0;
      let saleSum = 0;
      let meanRatio = 0;
      let count = 0;
      for (let x = 0; x < rowData.length; x++) {
        if (!rowData[x].outlier) {
          marketSum += rowData[x].marketValue;
          saleSum += rowData[x].salePriceAdjusted;
          count++;
          meanRatio += rowData[x].salePriceAdjusted
            ? rowData[x].marketValue / rowData[x].salePriceAdjusted
            : 0;
        }
      }
      meanRatio = count !== 0 ? meanRatio / count : 0;
      const weightedMean = marketSum / saleSum;
      prd = weightedMean !== 0 ? meanRatio / weightedMean : 0;
      body.results[0].submittedAnalysisStatistics.prd = prd.toFixed(2);
      body.results[0].submittedAnalysisStatistics.weightedMean = weightedMean.toFixed(
        4,
      );
    }
  }
  return body;
}

export const uploadMarketAreaFile = async (
  { appraisalService },
  authenticationToken,
  { year, marketArea, payload },
) => {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/appealevidencefiles`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const updateMarketAreaFile = async (
  { appraisalService },
  authenticationToken,
  { year, marketArea, payload },
) => {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/appealevidencefiles`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadMarketAreaFile = async (
  { appraisalService },
  authenticationToken,
  { s3ID, year, marketArea },
) => {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/appealevidencefile/${s3ID}`;
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const deleteMarketAreaFile = async (
  { appraisalService },
  authenticationToken,
  { s3ID, year, marketArea },
) => {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}/appealevidencefile/${s3ID}`;
  const { body } = await del(authenticationToken, url);
  return body;
};

export const fetchMarketAreaFiles = async (
  { appraisalService },
  authenticationToken,
  { year, marketArea },
) => {
  const url = `${appraisalService}/appraisal/marketareadetail/${marketArea}/${year}`;
  let { body } = await get(authenticationToken, url);
  return body;
};
